"use client";

import { cn } from "@/lib/utils";

export type PillSize = "sm" | "md";
export type PillIconPosition = "left" | "right";

export interface GlassPillProps {
  /** Icon component to display */
  icon?: React.ComponentType<{ className?: string }>;
  /** Position of the icon */
  iconPosition?: PillIconPosition;
  /** Size variant */
  size?: PillSize;
  /** Additional CSS classes */
  className?: string;
  /** Content */
  children: React.ReactNode;
}

const sizeClasses: Record<
  PillSize,
  { pill: string; icon: string; text: string }
> = {
  sm: {
    pill: "px-2 py-0.5 gap-1",
    icon: "h-3 w-3",
    text: "text-xs",
  },
  md: {
    pill: "px-3 py-1 gap-1",
    icon: "h-4 w-4",
    text: "text-sm",
  },
};

/**
 * GlassPill - Glass-style pill/tag for stats and metadata
 *
 * Use on dark backgrounds (images, gradients) to display information
 * like member counts, dates, prices, etc.
 *
 * @example
 * // On a hero image
 * <GlassPill icon={Users} size="md">
 *   42 members
 * </GlassPill>
 *
 * // Multiple pills in a row
 * <div className="flex gap-2">
 *   <GlassPill icon={Calendar}>Mar 15</GlassPill>
 *   <GlassPill icon={DollarSign}>$25</GlassPill>
 * </div>
 */
export function GlassPill({
  icon: Icon,
  iconPosition = "left",
  size = "md",
  className,
  children,
}: GlassPillProps) {
  const sizeConfig = sizeClasses[size];

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-full",
        "bg-white/20 backdrop-blur-sm",
        sizeConfig.pill,
        sizeConfig.text,
        className
      )}
    >
      {Icon && iconPosition === "left" && <Icon className={sizeConfig.icon} />}
      <span className="font-semibold">{children}</span>
      {Icon && iconPosition === "right" && <Icon className={sizeConfig.icon} />}
    </div>
  );
}

export default GlassPill;
